import { KeyboardEvent, useEffect, useRef, useState } from 'react';
import { SearchableEntityType } from '../globalSearch.types';
import { GlobalSearchListItem } from '../staticCommands/staticCommands.types';

const OPTION_DOM_ID_PREFIX = 'global-search-option';

export const getOptionDomId = (index: number): string => `${OPTION_DOM_ID_PREFIX}-${index}`;

interface UseGlobalSearchKeyboardNavParams {
  items: GlobalSearchListItem[];
  activeTabId: string;
  resetKey: string;
  query: string;
  isSearchSettled: boolean;
  canOpenEntity: (entityType: SearchableEntityType) => boolean;
  onSelectItem: (item: GlobalSearchListItem) => void;
  onActivateItemAction: (item: GlobalSearchListItem) => void;
}

interface PendingEnter {
  query: string;
  isItemAction: boolean;
}

const isItemDisabled = (
  item: GlobalSearchListItem,
  canOpenEntity: (entityType: SearchableEntityType) => boolean,
): boolean => item.kind === 'entity' && !canOpenEntity(item.item.entityType);

interface FindNextEnabledIndexParams {
  items: GlobalSearchListItem[];
  fromIndex: number;
  direction: 1 | -1;
  canOpenEntity: (entityType: SearchableEntityType) => boolean;
}

const findNextEnabledIndex = ({
  items,
  fromIndex,
  direction,
  canOpenEntity,
}: FindNextEnabledIndexParams): number => {
  let candidateIndex = fromIndex + direction;
  while (candidateIndex >= 0 && candidateIndex < items.length) {
    if (!isItemDisabled(items[candidateIndex], canOpenEntity)) {
      return candidateIndex;
    }
    candidateIndex += direction;
  }
  return fromIndex;
};

const useGlobalSearchKeyboardNav = ({
  items,
  activeTabId,
  resetKey,
  query,
  isSearchSettled,
  canOpenEntity,
  onSelectItem,
  onActivateItemAction,
}: UseGlobalSearchKeyboardNavParams) => {
  const [activeIndex, setActiveIndex] = useState(-1);
  const [isKeyboardNavActive, setIsKeyboardNavActive] = useState(false);
  const pendingEnterRef = useRef<PendingEnter | null>(null);

  useEffect(() => {
    setActiveIndex(-1);
  }, [activeTabId, resetKey]);

  useEffect(() => {
    setActiveIndex((previous) => {
      if (previous !== -1 && items[previous] && !isItemDisabled(items[previous], canOpenEntity)) {
        return previous;
      }
      return findNextEnabledIndex({ items, fromIndex: -1, direction: 1, canOpenEntity });
    });
  }, [items, canOpenEntity]);

  useEffect(() => {
    const pendingEnter = pendingEnterRef.current;
    if (!pendingEnter || !isSearchSettled) {
      return;
    }
    pendingEnterRef.current = null;
    const firstEnabledItem =
      items[findNextEnabledIndex({ items, fromIndex: -1, direction: 1, canOpenEntity })];
    if (pendingEnter.query !== query || !firstEnabledItem) {
      return;
    }
    const submit = pendingEnter.isItemAction ? onActivateItemAction : onSelectItem;
    submit(firstEnabledItem);
  }, [isSearchSettled, query, items, canOpenEntity, onSelectItem, onActivateItemAction]);

  useEffect(() => {
    document.getElementById(getOptionDomId(activeIndex))?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const handleMouseMove = () => {
    setIsKeyboardNavActive(false);
  };

  const handleEnter = (event: KeyboardEvent<HTMLElement>) => {
    const isItemAction = event.metaKey || event.ctrlKey;
    if (!isSearchSettled) {
      event.preventDefault();
      pendingEnterRef.current = { query, isItemAction };
      return;
    }
    const activeItem = items[activeIndex];
    if (!activeItem || isItemDisabled(activeItem, canOpenEntity)) {
      return;
    }
    event.preventDefault();
    const submit = isItemAction ? onActivateItemAction : onSelectItem;
    submit(activeItem);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (items.length === 0) {
          break;
        }
        setIsKeyboardNavActive(true);
        setActiveIndex((previous) =>
          findNextEnabledIndex({ items, fromIndex: previous, direction: 1, canOpenEntity }),
        );
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (items.length === 0) {
          break;
        }
        setIsKeyboardNavActive(true);
        setActiveIndex((previous) =>
          findNextEnabledIndex({ items, fromIndex: previous, direction: -1, canOpenEntity }),
        );
        break;
      case 'Enter':
        handleEnter(event);
        break;
      default:
        break;
    }
  };

  const activeDescendantId = items[activeIndex] ? getOptionDomId(activeIndex) : undefined;

  return { activeIndex, activeDescendantId, isKeyboardNavActive, handleKeyDown, handleMouseMove };
};

export default useGlobalSearchKeyboardNav;
